import { _NEW_LINE, _cardWidthMax, _cardWidthMin, _defaultParams, charsMappingFromURL } from "../components/constants";
import { longestWordInList, longestWordLength } from "./text";

export interface CardStyle {
  display: string;
  flexDirection: string;
  justifyContent: string;
  backgroundColor: string;
  border: string;
  boxShadow: string;
  borderRadius: string;
  width: string;
  minWidth: string;
  height: string;
  maxWidth: string;
  margin: string;
  fontSize: string;
  minHeight: string;
}

export const getCardStyle = (
  cardSize: number ,
  fontSize: number,
  invertImgTxt?: boolean | false | null ,
  backgroundColor?: string | "white",
  isBorderHighlighted?: boolean | false,
  isBorderBlack?: boolean | false
): CardStyle => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: invertImgTxt === null ? 'center' : invertImgTxt === false ? 'flex-end' : 'flex-start',
  backgroundColor: backgroundColor ? backgroundColor : "white",
  border: isBorderHighlighted ? "4px red solid" : isBorderBlack ? "2px black solid" : "1px lightgray solid",  
  boxShadow: "0 4px 8px 0 rgba(0,0,0,0.2)",
  borderRadius: "5px",
  width: `${cardSize}px`,
  minWidth: `${cardSize}px`,
  height: "100%",
  minHeight: `${cardSize}px`,
  maxWidth: `${cardSize}px`,
  margin: "1px",
  fontSize: `${fontSize}px`,
  
});


export function calculateCardAndFontSize(
    cardSizeMultiplier: number,
    visibleLabels: string[],
    _cardFontSizeMin: number,
    _cardFontSizeMax: number,
    _cardWidthMin: number,
    _cardWidthMax: number,
    labelToShow: string)
  : { CW: number; FS: number; } {

    return { CW: 120, FS: 14 };
    let CW = 0;
    let FS = 0;

    let cardsNumber = visibleLabels ? visibleLabels.length : 0;
    let longestWordLengthOverall = longestWordInList(visibleLabels);
    if(cardsNumber === 0){return {CW, FS};}
    let minFS = _cardFontSizeMin;
    let maxFS = _cardFontSizeMax;
    let minCW = _cardWidthMin;
    let maxCW = _cardWidthMax;
    let WL = longestWordLength(labelToShow);

    /* To give cards more uniformity */
    if(WL < longestWordLengthOverall){
      // mean value
      WL = Math.ceil((WL + longestWordLengthOverall)/2);
      //WL =  longestWordLengthOverall;
    }

    FS = 15 * cardSizeMultiplier;
    if (FS > maxFS) {
        FS = maxFS;
    }

    // Calculate a new variable that increases as cardSizeMultiplier increases
    let newVar = cardSizeMultiplier;
    if (FS === maxFS) {
        newVar *= 2; // Increase at a faster rate when FS is at its maximum
    }

    // Calculate CW with the new variable
    CW = 0.66 * FS * WL * newVar;
    if (CW < minCW) {
        CW = minCW;
    } else if (CW > maxCW) {
        CW = maxCW;
    }
    
    return { CW, FS };
}


export function label2LabelToShow(label: string, lang: string, capitalizeText: number = 1): string {
    let labelToShow = charsMappingFromURL(label, lang);
    if(capitalizeText === 2){
    labelToShow = labelToShow.toUpperCase();
    }else if(capitalizeText === 3){
    labelToShow = labelToShow.toLowerCase();
    }else if(capitalizeText === 4){
    labelToShow = "";
    }

    return labelToShow;
}


export const createLabelPositionsMap = (labels:string[]) => {
    const labelPositions = new Map();
    labels.forEach((label, idx) => {
      if (!labelPositions.has(label)) {
        labelPositions.set(label, []);
      }
      labelPositions.get(label).push(idx);
    });
    return labelPositions;
  };

/**
 * Corrects a sentence by applying specific rules.
 * - Converts words starting with _NEW_LINE to _NEW_LINE.
 * - Removes consecutive _NEW_LINE elements.
 * - Removes null and undefined elements
 * @param {string[]} sentence - The array of words to correct.
 * @return {string[]} - The corrected array of words.
 */
export const correctSentence = (sentence: string[]): string[] => {
  sentence = sentence.filter((word) => word != null && word !== undefined);
  let writeIndex = 0;  // Index to write the next valid word
  let prevWasNewLine = false; 
  for (let readIndex = 0; readIndex < sentence.length; readIndex++) {
    const word = sentence[readIndex];
    const isCurrentNewLine = word?.startsWith(_NEW_LINE);

    if (isCurrentNewLine) {
      if (!prevWasNewLine) {
        sentence[writeIndex++] = _NEW_LINE;
      }
      prevWasNewLine = true;
    } else {
      sentence[writeIndex++] = word;
      prevWasNewLine = false;
    }
  }

  // Truncate the array to remove unused slots
  sentence.length = writeIndex;
  return sentence;
};

interface CardDimensions {
  width: number;
  height: number;
}

interface ImageDimensions {
  width: number;
  height: number;
}

interface TextDimensions {
  fontSize: number;
  maxChars: number;
}

function calculateCardDimensions(
  cardWidth: number,
  cardHeight: number,
  imageWidth: number,
  imageHeight: number,
  textDimensions: TextDimensions,
  aspectRatio: number
): CardDimensions {
  // Calculate image dimensions
  const imageAspectRatio = imageWidth / imageHeight;
  let calculatedImageWidth, calculatedImageHeight;

  if (imageAspectRatio > aspectRatio) {
    calculatedImageWidth = cardWidth;
    calculatedImageHeight = cardWidth / imageAspectRatio;
  } else {
    calculatedImageHeight = cardHeight;
    calculatedImageWidth = cardHeight * imageAspectRatio;
  }

  // Calculate text dimensions
  const maxCharsPerLine = Math.floor(cardWidth / (textDimensions.fontSize * 0.6)); // Adjust as needed
  const maxLines = Math.floor(cardHeight / textDimensions.fontSize); // Adjust as needed
  const maxChars = maxCharsPerLine * maxLines;

  // Adjust card dimensions
  const calculatedCardWidth = Math.max(calculatedImageWidth, textDimensions.fontSize * maxCharsPerLine);
  const calculatedCardHeight = Math.max(calculatedImageHeight, textDimensions.fontSize * maxLines);

  return { width: calculatedCardWidth, height: calculatedCardHeight };
}

// Example usage:
/*
const cardWidth = 300;
const cardHeight = 400;
const imageWidth = 200;
const imageHeight = 150;
const textDimensions = { fontSize: 14, maxChars: 50 };
const aspectRatio = cardWidth / cardHeight;

const calculatedDimensions = calculateCardDimensions(
  cardWidth,
  cardHeight,
  imageWidth,
  imageHeight,
  textDimensions,
  aspectRatio
);

console.log(calculatedDimensions);
*/